import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import {
  RecordData,
  RecordUpdateRequestParams,
  SelectData
} from "../../../../../types/records-table.types";
import {
  bankDetailsResourceType,
  dateFields,
  deleteBankDetailsConfirmationParams,
  unbindBankDetailsConfirmationParams
} from "../../configs/bank-details-modal-config";
import {FormGroup} from "@angular/forms";
import {BaseConfirmModalComponent} from "../../../base-confirm-modal/base-confirm-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {BankDetailsModalService} from "../../services/bank-details-modal.service";
import {FormFieldUpdate} from "../../../../../types/file.types";
import {BankDetailsDialogData} from "../../types/bank-details-modal.types";
import {ActionDropdownMenuComponent} from "../../../../action-dropdown-menu/action-dropdown-menu.component";
import {BankDetailsFormComponent} from "../bank-details-form/bank-details-form.component";
import {defaultRecordData} from "../../../../../models/default-record-data";
import {MatButton} from "@angular/material/button";
import {BankDetailsFormField} from "../../types/bank-details-form-field.types";
import {bankDetailsFormFields} from "../../configs/bank-details-form-fields";
import {BaseModalService} from "../../../services/base-modal.service";
import {CloseModalReason} from "../../../../../types/modal.types";
import {MatExpansionModule} from "@angular/material/expansion";
import {GetRecordPropertyDataByIdParams} from "../../../base-modal/types/base-modal.types";
import {DetachProps} from "../../../../../types/attach-detach.types";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-current-bank-details',
  standalone: true,
  imports: [
    ActionDropdownMenuComponent,
    BankDetailsFormComponent,
    MatButton,
    MatExpansionModule,
    LoaderComponent
  ],
  templateUrl: './current-bank-details.component.html',
  styleUrls: [
    './current-bank-details.component.scss',
    '../../bank-details-modal.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentBankDetailsComponent implements OnInit {
  bankDetailsDialogData = input<BankDetailsDialogData>();
  legalEntityTypes = input<Array<SelectData>>([]);
  isEditModeActive = input<boolean>(false);
  unbindBankDetails = output<void>();
  closeDialog = output<CloseModalReason>();

  private bankDetailsModalService: BankDetailsModalService = inject(BankDetailsModalService);
  private dialog: MatDialog = inject(MatDialog);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public bankDetailsFormGroups = signal<FormGroup[]>([]);
  public bankDetailsData = signal<RecordData[]>([]);
  public isBankDetailsFormValid = signal<boolean>(false);
  public recordCalendarId = computed(() => this.baseModalService.getRecordCalendarId(this.bankDetailsDialogData()?.recordData || defaultRecordData));
  public bloggerId = computed(() => this.bankDetailsDialogData()?.bloggerId || 0);

  public async ngOnInit(): Promise<void> {
    this.bankDetailsDialogData()?.fromBloggerProfile ?
      await this.fetchBankDetailsData() :
      await this.fetchAndPatchExistingPropertyData();
  }

  public handleDeleteOrUnbind(index?: number): void {
    this.dialog
      .open(BaseConfirmModalComponent, {
        data: {
          deleteConfirmationParams: index !== undefined ?
            deleteBankDetailsConfirmationParams :
            unbindBankDetailsConfirmationParams
        },
      })
      .afterClosed().subscribe(async (result) =>
        result === 'remove' &&
          index !== undefined ?
            await this.deleteBankDetails(index) :
            this.handleUnbindBankDetails()
    );
  }

  public handleRecordUpdate(formField: FormFieldUpdate, index: number): void {
    if (!this.bankDetailsDialogData()?.recordData['bankDetails']) { return; }

    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: bankDetailsResourceType.path,
      recordId: Number((this.bankDetailsData().at(index) as RecordData).id),
      fieldName: formField.key,
      value: formField.newValue,
    };

    this.bankDetailsModalService
      .updatedRecordField<RecordData>(recordUpdateRequestParams)
      .then((updatedData) => {
        const convertedData = this.baseModalService.convertDates<RecordData>(updatedData, dateFields);
        this.bankDetailsFormGroups().at(index)?.patchValue(convertedData);
        this.bankDetailsData.update(data => data.map((item, i) => i === index ? updatedData : item));
      });
  }

  private async fetchBankDetailsData(): Promise<void> {
    const recordsTableData = await this.bankDetailsModalService.fetchBankDetailsData(this.bloggerId());

    this.bankDetailsData.set(recordsTableData.content);
    this.bankDetailsFormGroups.set(this.bankDetailsModalService.setFormGroups(this.bankDetailsData()));
    this.isBankDetailsFormValid.set(true);
  }

  private async deleteBankDetails(index: number): Promise<void> {
    const bankDetails = this.bankDetailsData().at(index) as RecordData;
    await this.bankDetailsModalService.deleteRecord(bankDetailsResourceType.path, bankDetails.id);
    this.bankDetailsFormGroups.update((groups) =>
      groups.filter((_, i) => i !== index)
    );
    this.bankDetailsData.update((data) => data.filter((_, i) => i !== index));
  }

  private async fetchAndPatchExistingPropertyData(): Promise<void> {
    const params: GetRecordPropertyDataByIdParams = {
      path: bankDetailsResourceType.path,
      resourceId: Number((this.bankDetailsDialogData()?.recordData['bankDetails'] as RecordData).id),
    };

    const data = await this.bankDetailsModalService.fetchDataById<RecordData>(params);
    this.bankDetailsData.update((current) => [...current, data]);
    const formGroup = this.baseModalService.getFormGroup<BankDetailsFormField>(bankDetailsFormFields);
    const bankDetailsData = this.baseModalService.convertDates<RecordData>(data, dateFields);
    formGroup.patchValue(bankDetailsData);

    this.bankDetailsFormGroups.update((current) => [...current, formGroup]);
    this.isBankDetailsFormValid.set(true);
  }

  private async handleUnbindBankDetails(): Promise<void> {
    const detachProps: DetachProps = {
      path: bankDetailsResourceType.path,
      recordCalendarId: this.recordCalendarId(),
    };

    await this.bankDetailsModalService.detachRecordData(detachProps).catch(() => defaultRecordData);
    this.unbindBankDetails.emit();
  }
}
